import { useState } from "react";
import { useRouter } from "next/router";
import { UrlObject } from "url";
import { useProjects } from "components/modules/Projects/lib/hooks/useProjects";
import { useCurrentProject } from "lib/hooks/useProject";
import ChevronDown from "components/icons/24/actions/chevron-down.svg";
import ChevronUp from "components/icons/24/actions/chevron-up.svg";
import Dropdown from "components/elements/Dropdown/Dropdown";
import ListItemProjectDropdown from "components/elements/ListItem/ListItemProjectDropdown";
import MessageBanner, {
  MessageType,
} from "components/elements/MessageBanner/MessageBanner";
import ArrowRightSvg from "components/icons/24/actions/arrow-right-circle.svg";
import { useCesium } from "components/elements/CesiumViewer/core/useCesium";
import { filterProjectsByRecent } from "lib/actions/recentProjects/filterProjectsByRecent";
import LoadingSkeleton from "components/elements/Loading/LoadingSkeleton";
import ProjectSvg from "components/icons/24/workspace_project.svg";

export default function ProjectDropdown(p: { isCompact?: boolean }) {
  const [isOpen, setIsOpen] = useState(false);

  const router = useRouter();
  const {
    isProjectOpen,
    project: currentProject,
    isLoading: isCurrentProjectLoading,
  } = useCurrentProject();
  const { projects, isLoading: isProjectsLoading } = useProjects();
  const { selectedElementPlugin, assetsPlugin, toolManager } = useCesium();
  const isLoading = isCurrentProjectLoading || isProjectsLoading;

  const projectsWithoutCurrent = currentProject
    ? projects?.filter((project) => project.id != currentProject.id)
    : projects;
  const projectsRecent = filterProjectsByRecent(projectsWithoutCurrent);

  function toggleOpen() {
    setIsOpen((isOpen) => !isOpen);
  }

  function resetSpatialView() {
    selectedElementPlugin.clearSelection();
    assetsPlugin.hideAll();
    assetsPlugin.cancelPendingFlyTo();

    // TODO: When a tool is active, it will only clear the tool, not collapse the tool drawer. The tool drawer is not part of a context, so it's not possible to reset currently
    toolManager.resetAllTools();
  }

  function navigateToAllProjects() {
    resetSpatialView();

    router.push({
      pathname: "/projects",
    } as UrlObject);
  }

  function switchProject(id: number) {
    setIsOpen(false);
    resetSpatialView();

    router.push({
      pathname: "/projects/[projectId]",
      query: { projectId: id },
    } as UrlObject);
  }

  if (!isProjectOpen) return null;

  if ((!currentProject || !projects?.length) && isLoading) {
    return (
      <Dropdown
        variant="flat"
        className="self-stretch"
        dropdownWidth={380}
        dropdownMaxHeight="none"
        buttonContent={
          <div className="mx-1 flex items-center space-x-2">
            <LoadingSkeleton className="h-4 w-32" />
            <div className="w-5">
              {isOpen ? <ChevronUp /> : <ChevronDown />}
            </div>
          </div>
        }
        isOpen={isOpen}
        toggleOpen={toggleOpen}
        dropdownContent={
          <div className="p-3">
            <p className="mb-3 ml-1 mt-1 text-xs text-gray-400">
              Recent Projects
            </p>
            {[...Array(3)].map((_, index) => (
              <div key={index} className="my-2">
                <div className="flex items-center space-x-3 p-2">
                  <LoadingSkeleton className="h-4 w-full" />
                </div>
              </div>
            ))}
            <div className="mt-1 flex flex-row-reverse text-sm">
              <a
                className="flex cursor-pointer flex-row items-center px-1 pt-1"
                onClick={navigateToAllProjects}
              >
                Projects List
                <div className="flex aspect-square w-8 items-center px-1 ">
                  <ArrowRightSvg className="h-6 w-6" />
                </div>
              </a>
            </div>
          </div>
        }
      />
    );
  }

  return (
    <Dropdown
      variant="flat"
      // Compact, it's a square button holding just the project icon.
      className={p.isCompact ? "h-10 w-10 !p-0" : "self-stretch"}
      dropdownWidth={380}
      dropdownMaxHeight="none"
      buttonContent={
        p.isCompact ? (
          <div title={currentProject?.name} aria-label={currentProject?.name}>
            <ProjectSvg className="h-6 w-6" />
          </div>
        ) : (
          <div className="mx-1 flex items-center space-x-2">
            <span>{currentProject?.name}</span>
            <div className="w-5">
              {isOpen ? <ChevronUp /> : <ChevronDown />}
            </div>
          </div>
        )
      }
      isOpen={isOpen}
      toggleOpen={toggleOpen}
      dropdownContent={
        <div className="p-3">
          <p className="mb-3 ml-1 mt-1 text-xs text-gray-400">
            Recent Projects
          </p>
          {projectsRecent?.map((project) => (
            <div
              key={project.id}
              className="my-2 cursor-pointer hover:bg-gray-50"
              onClick={() => switchProject(project.id)}
            >
              <ListItemProjectDropdown
                title={project.name}
                projectWorkspace={project.workspace}
              />
            </div>
          ))}
          {!projectsRecent?.length && (
            <MessageBanner messageType={MessageType.Info} colourWidth={"sm"}>
              <div className="mb-2 text-xs font-medium text-primary">
                You haven&apos;t visited any other projects recently.
              </div>
              <div className=" text-xs font-light text-primary">
                Return to your Projects List to see the other projects you have
                access to.
              </div>
            </MessageBanner>
          )}
          <div className="mt-1 flex flex-row-reverse text-sm">
            <a
              className="flex cursor-pointer flex-row items-center px-1 pt-1"
              onClick={navigateToAllProjects}
            >
              Projects List
              <div className="flex aspect-square w-8 items-center px-1 ">
                <ArrowRightSvg className="h-6 w-6" />
              </div>
            </a>
          </div>
        </div>
      }
    />
  );
}
